import { LinearGradient } from 'expo-linear-gradient';
import type { StyleProp, ViewStyle } from 'react-native';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { radii, rippleOnOutline, rippleOnPrimary, spacing, useAppTheme } from '@/theme';

// Equal-width footer actions leave ~100dp per label on a 390dp screen, which a long
// full-width-glyph label overflows on one line, so labels may use a second line.
const MAX_LABEL_LINES = 2;

interface ButtonProps {
  readonly variant: 'primary' | 'outline';
  readonly onPress: () => void;
  readonly children: string;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly style?: StyleProp<ViewStyle>;
  readonly testID?: string;
}

export const Button = ({
  variant,
  onPress,
  children,
  disabled = false,
  loading = false,
  style,
  testID,
}: ButtonProps) => {
  const { colors } = useAppTheme();
  const isPrimaryDisabled = variant === 'primary' && disabled;
  const buttonOpacity = disabled && !isPrimaryDisabled ? styles.disabled : null;
  const activityIndicatorColor = variant === 'primary' ? colors.onPrimary : colors.brandPurpleDark;
  const textStyle = variant === 'primary' ? styles.text : [styles.text, styles.outlineText];
  const primaryTextColor = isPrimaryDisabled ? colors.textNdDisabled : colors.onPrimary;
  const textColor = variant === 'primary' ? primaryTextColor : colors.brandPurpleDark;

  const rippleColor = variant === 'primary' ? rippleOnPrimary : rippleOnOutline;

  const content = (
    <View style={styles.content}>
      {loading ? (
        <ActivityIndicator
          accessibilityRole="progressbar"
          color={activityIndicatorColor}
          size="small"
          testID="button-loading-indicator"
        />
      ) : null}
      <Text numberOfLines={MAX_LABEL_LINES} style={[textStyle, { color: textColor }]}>
        {children}
      </Text>
    </View>
  );

  const pressableStyle = ({ pressed }: { pressed: boolean }) => [
    styles.pressable,
    buttonOpacity,
    pressed && styles.pressed,
    style,
  ];

  if (variant === 'primary') {
    const surfaceTestID = testID ? `${testID}-surface` : undefined;

    return (
      <Pressable
        accessibilityRole="button"
        android_ripple={{ color: rippleColor }}
        disabled={disabled}
        onPress={onPress}
        style={pressableStyle}
        testID={testID}>
        {isPrimaryDisabled ? (
          <View
            style={[styles.gradientContainer, { backgroundColor: colors.bgNlStronger }]}
            testID={surfaceTestID}>
            {content}
          </View>
        ) : (
          <LinearGradient
            colors={[colors.brandPurpleLight, colors.primary]}
            end={{ x: 0.5, y: 1 }}
            start={{ x: 0.5, y: 0 }}
            style={styles.gradientContainer}
            testID={surfaceTestID}>
            {content}
          </LinearGradient>
        )}
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      android_ripple={{ color: rippleColor }}
      disabled={disabled}
      onPress={onPress}
      style={pressableStyle}
      testID={testID}>
      <View style={[styles.container, styles.outline, { borderColor: colors.primary }]}>
        {content}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'stretch',
    borderRadius: radii.sm,
    overflow: 'hidden',
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing[2],
    justifyContent: 'center',
  },
  container: {
    minHeight: spacing[10],
    borderWidth: 1,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  gradientContainer: {
    minHeight: spacing[10],
    alignItems: 'center',
    justifyContent: 'center',
  },
  outline: {
    paddingHorizontal: spacing[2],
  },
  text: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  outlineText: {
    lineHeight: 20,
    letterSpacing: 0.1,
  },
  disabled: {
    opacity: 0.5,
  },
  pressed: {
    opacity: 0.75,
  },
});
